import React, { useState } from 'react';
import {
  Box,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  Icon,
  HStack,
  Spinner,
  Button,
  useColorModeValue,
  keyframes
} from '@chakra-ui/react';
import { FiSearch, FiX } from 'react-icons/fi';

// Animación del anillo pulsante para búsqueda activa
const pulseRing = keyframes`
  0% {
    transform: scale(0.33);
  }
  40%,
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: scale(1.33);
  }
`;

const UnifiedSearchInput = ({
  value = '',
  onChange,
  onClear,
  placeholder = "Buscar por nombre, apellido, email, username o grupo...",
  isSearching = false,
  size = "lg",
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange('');
    }
  };

  return (
    <Box position="relative" w="full">
      <InputGroup size={size} w="full">
        <InputLeftElement 
          height="full"
          pointerEvents={isSearching ? 'none' : 'auto'}
        >
          {isSearching ? (
            <Box position="relative">
              <Icon 
                as={FiSearch} 
                color="blue.400" 
                boxSize={5}
              />
              <Box
                position="absolute"
                top="-2px"
                left="-2px"
                width="24px"
                height="24px"
                border="2px solid"
                borderColor="blue.400"
                borderRadius="50%"
                opacity="0.6"
                animation={`${pulseRing} 1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite`}
              />
            </Box>
          ) : (
            <Icon 
              as={FiSearch} 
              color={value || isFocused ? "blue.500" : "gray.400"} 
              boxSize={5}
              transition="all 0.2s"
            />
          )}
        </InputLeftElement>
        
        <Input
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          fontSize="md"
          fontWeight="500"
          bg="cream.bg"
          border="1.5px solid"
          borderColor={value || isFocused ? 'coral.500' : 'cream.line2'}
          borderRadius="11px"
          color="ink.700"
          _hover={{ borderColor: 'ink.400' }}
          _focus={{
            borderColor: 'coral.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-coral-500)',
          }}
          _placeholder={{ color: 'ink.400', fontSize: 'md' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          transition="all 0.15s"
        />
        
        {value && (
          <InputRightElement height="full" pr={2}>
            <HStack spacing={1}>
              {isSearching && (
                <Spinner size="sm" color="coral.500" thickness="3px" />
              )}
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                minW="auto"
                h="32px"
                w="32px"
                onClick={handleClear}
                borderRadius="full"
                transition="all 0.2s"
                _hover={{
                  bg: 'gray.100',
                  transform: 'scale(1.1)'
                }}
                isDisabled={disabled}
              >
                <Icon as={FiX} boxSize={4} />
              </Button>
            </HStack>
          </InputRightElement>
        )}
      </InputGroup>
    </Box>
  );
};

export default UnifiedSearchInput;